import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchOfferings,
  purchasePackage,
  restorePurchases,
  checkSubscriptionStatus,
  clearPurchaseStatus,
  clearRestoreStatus,
  clearError,
  selectIsPeakSub,
  selectIsAdFree,
  selectAvailableOfferings,
  selectCurrentOffering,
  selectActiveSubIDs,
  selectPurchaseStatus,
  selectRestoreStatus,
} from '../store/slices/premiumSliceSimple';
import { PurchasesPackage } from '@revenuecat/purchases-typescript-internal-esm';

/**
 * Simple premium hook following original app pattern
 */
const usePremiumSimple = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Core subscription state
  const isPremium = useAppSelector(selectIsPeakSub);
  const isAdFree = useAppSelector(selectIsAdFree);
  const offerings = useAppSelector(selectAvailableOfferings);
  const currentOffering = useAppSelector(selectCurrentOffering);
  const activeSubIDs = useAppSelector(selectActiveSubIDs);
  const purchaseStatus = useAppSelector(selectPurchaseStatus);
  const restoreStatus = useAppSelector(selectRestoreStatus);

  /**
   * Load offerings on mount (if not already loaded)
   */
  useEffect(() => {
    if (offerings.length === 0) {
      dispatch(fetchOfferings());
    }
  }, [dispatch, offerings.length]);

  /**
   * Handle purchase
   */
  const handlePurchase = useCallback(
    (packageToPurchase: PurchasesPackage) => {
      dispatch(purchasePackage(packageToPurchase));
    },
    [dispatch]
  );

  /**
   * Restore purchases
   */
  const handleRestorePurchases = useCallback(() => {
    dispatch(restorePurchases());
  }, [dispatch]);

  /**
   * Check subscription status
   */
  const refreshStatus = useCallback(() => {
    dispatch(checkSubscriptionStatus());
  }, [dispatch]);

  /**
   * Clear purchase status
   */
  const resetPurchaseStatus = useCallback(() => {
    dispatch(clearPurchaseStatus());
  }, [dispatch]);

  /**
   * Clear restore status
   */
  const resetRestoreStatus = useCallback(() => {
    dispatch(clearRestoreStatus());
  }, [dispatch]);

  /**
   * Clear any errors
   */
  const resetError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  /**
   * Navigate to premium landing
   */
  const goToPremiumLanding = useCallback(() => {
    navigate('/premium');
  }, [navigate]);

  /**
   * Check if a package is active (already purchased)
   */
  const isPackageActive = useCallback(
    (packageId: string) => {
      return activeSubIDs.includes(packageId);
    },
    [activeSubIDs]
  );

  return {
    // Subscription status
    isPremium,
    isAdFree,

    // Offerings
    offerings,
    currentOffering,
    activeSubIDs,

    // Status
    purchaseStatus,
    restoreStatus,

    // Actions
    handlePurchase,
    handleRestorePurchases,
    refreshStatus,
    resetPurchaseStatus,
    resetRestoreStatus,
    resetError,
    goToPremiumLanding,
    isPackageActive,
  };
};

export default usePremiumSimple;